import { AST } from '@handlebars/parser';

import { addPathDepth } from 'utils/urls';
import { isString } from 'utils/helpers';
import { ElementOf } from 'utils/tsHelpers';
import { joinWithDots, parenthesize, joinWithSpaces, joinWithEquals } from 'utils/stringHelpers';

import { isPathExpression, isSubExpression } from './utils';
import { isLiteral } from '../literal/utils';
import { LiteralSerializer } from '../literal/serializer';
import { HANDLEBARS_DATA_PATH_PREFIX } from './constants';

/**
 * Utility class for converting `AST.Expression` to a Handlebars template string
 */
export class ExpressionSerializer {
  /**
   * Serializes any type of `AST.Expression` into a Handlebars template sting
   */
  static serialize = (expression: AST.Expression): string => {
    if (isPathExpression(expression))
      return ExpressionSerializer.serializePathExpression(expression);
    if (isSubExpression(expression)) return ExpressionSerializer.serializeSubExpression(expression);
    if (isLiteral(expression)) return LiteralSerializer.serialize(expression);

    throw new Error('Could not serialize expression');
  };

  /**
   * Serializes `AST.PathExpression` into a Handlebars template sting
   */
  private static serializePathExpression = (expression: AST.PathExpression) =>
    expression.data
      ? ExpressionSerializer.serializeDataPath(expression)
      : ExpressionSerializer.serializePath(expression);

  /**
   * Serializes `AST.SubExpression` into a Handlebars template sting
   */
  private static serializeSubExpression = (expression: AST.SubExpression) =>
    parenthesize(
      joinWithSpaces([
        ExpressionSerializer.serialize(expression.path),
        ExpressionSerializer.serializeSubExpressionParams(expression.params),
        ExpressionSerializer.serializeHash(expression.hash),
      ]),
    );

  // Private helper functions:
  private static serializePath = (expression: AST.PathExpression) =>
    addPathDepth(
      ExpressionSerializer.serializePathExpressionParts(expression.parts),
      expression.depth,
    );

  private static serializeDataPath = (expression: AST.PathExpression) =>
    `${HANDLEBARS_DATA_PATH_PREFIX}${ExpressionSerializer.serializePath(expression)}`;

  private static serializePathExpressionParts = (parts: AST.PathExpression['parts']) =>
    joinWithDots(parts.map(ExpressionSerializer.serializePathExpressionPart));

  private static serializePathExpressionPart = (part: ElementOf<AST.PathExpression['parts']>) =>
    isString(part) ? part : ExpressionSerializer.serializeSubExpression(part);

  private static serializeSubExpressionParams = (params: AST.SubExpression['params']) =>
    joinWithSpaces(params.map(ExpressionSerializer.serialize));

  private static serializeHash = (hash: AST.Hash) =>
    joinWithSpaces(hash.pairs.map(ExpressionSerializer.serializeHashPair));

  private static serializeHashPair = ({ key, value }: AST.HashPair) =>
    joinWithEquals([key, ExpressionSerializer.serialize(value)]);
}
